// Quotes.js — Universal Business Testimonials
import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Composite, representative scenarios based on common patterns we hear from
// prospects and early users — not verbatim quotes from a named customer.
// Deliberately has no company name attached, so it never reads as a
// testimonial from a specific organisation we haven't actually gotten
// sign-off from.
const QUOTES = [
  {
    quote:
      "We thought our data was ‘good enough’ until a proper scan showed what was really going on. Catching those issues early is what stops a migration from slipping.",
    author: "Data Migration Lead",
    company: "Housing sector",
  },
  {
    quote:
      "Bad data slows everything down on the shop floor. Once inconsistencies we hadn't noticed for years were fixed, reporting and production planning finally lined up.",
    author: "Operations Director",
    company: "Manufacturing sector",
  },
  {
    quote:
      "Duplicates, old titles, mismatched metadata — it builds up over time. A clear, scored picture of what needs attention is what actually gets it fixed.",
    author: "Head of Data & Editorial Systems",
    company: "Publishing sector",
  },
];

export default function Quotes({
  title = "The kind of problem PrismDQ exists to fix.",
  subtitle = "Composite scenarios based on patterns we hear across industries — not attributed to a single named customer.",
  items = QUOTES,
  autoPlay = true,
  autoPlayMs = 5500,
}) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => next(), autoPlayMs);
    return () => clearInterval(id);
  }, [index, autoPlay, autoPlayMs]);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const total = items.length;
    const clamped = ((i % total) + total) % total;
    setIndex(clamped);

    const width = el.clientWidth;
    el.scrollTo({ left: clamped * width, behavior: "smooth" });
  };

  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  return (
    <section id="quotes" className="relative py-20 lg:py-24">
      {/* gradient backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_400px_at_50%_-10%,rgba(96,165,250,0.18),transparent)]" />
        <div className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(115deg,#000_1px,transparent_1px)] bg-[length:20px_20px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-8 max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
          {subtitle && <p className="mt-3 text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>

      <div className="relative">
        {/* fade edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white/90 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white/90 to-transparent" />

        {/* controls */}
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between px-2 sm:px-4">
          <button
            onClick={prev}
            className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/70 p-2 text-slate-700 shadow-sm backdrop-blur hover:bg-white"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/70 p-2 text-slate-700 shadow-sm backdrop-blur hover:bg-white"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* scrolling track */}
        <div
          ref={trackRef}
          className="flex snap-x snap-mandatory overflow-x-auto scroll-px-8 [scrollbar-width:none]"
          style={{ scrollBehavior: "smooth" }}
          onScroll={(e) => {
            const el = e.currentTarget;
            const width = el.clientWidth;
            const i = Math.round(el.scrollLeft / width);
            if (i !== index) setIndex(i);
          }}
        >
          {items.map((t, i) => (
            <Slide key={i} {...t} active={i === index} />
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`h-2.5 w-2.5 rounded-full transition ${
              i === index ? "bg-blue-600" : "bg-slate-300"
            }`}
            aria-label={`Go to testimonial ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

function Slide({ quote, author, company, active }) {
  return (
    <article className="snap-start shrink-0 w-full">
      <div className="mx-auto flex min-h-[44vh] max-w-7xl items-center px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: active ? 1 : 0.8, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full"
        >
          <blockquote className="text-balance text-2xl sm:text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
            <span className="align-top text-5xl sm:text-7xl md:text-8xl text-blue-600/30 select-none">
              “
            </span>
            <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-800 bg-clip-text text-transparent">
              {quote}
            </span>
            <span className="align-bottom text-5xl sm:text-7xl md:text-8xl text-blue-600/30 select-none">
              ”
            </span>
          </blockquote>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 ring-2 ring-white shadow-sm" />
            <div className="text-sm">
              <span className="font-semibold text-slate-900">{author}</span>
              <span className="mx-2 text-slate-400">/</span>
              <span className="text-slate-600">{company}</span>
            </div>
          </div>

          <div className="mt-6 h-2 w-40 overflow-hidden rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400">
            <div className="h-full w-full animate-[shine_2.6s_linear_infinite] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent)] bg-[length:160px_100%]" />
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes shine {
          0% { background-position: -160px 0; }
          100% { background-position: 160px 0; }
        }
      `}</style>
    </article>
  );
}